import { Injectable } from '@angular/core';
import { Store as ngxsStore } from '@ngxs/store';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import * as signalR from '@microsoft/signalr';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, firstValueFrom } from 'rxjs';
import { selectTokenInfo } from "app/reducers/context.selectors";
import { refreshLoginData, signOutUser } from 'app/reducers/context.actions';
import { ApiSignalUrl } from 'app/services/http/url-constants/hub-url-constants';
import { TokenInfo } from 'app/reducers/context.reducer';
import { APIService } from 'app/services/api.service';
import { EventService } from 'app/services/signalR/event.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private connection: signalR.HubConnection;
  private connectPromise: Promise<void> | null = null;
  private isConnected: boolean;
  private lastHashSelectedOrganizations = '';

  constructor(
    private ngxsStore: ngxsStore,
    private store: Store,
    private apiService: APIService,
    private eventService: EventService,
  ) {
  }

  public async initAsync(): Promise<void>
  {
    combineLatest(
      [
        this.store.select(selectTokenInfo),
        this.ngxsStore.select(OrganizationsState.getSelectedOrganizationTree),
      ]
    ).pipe(
      untilDestroyed(this),
    ).subscribe(async ([tokenInfo, selectedOrganizations]): Promise<void> => {
      if (!tokenInfo || !tokenInfo.token) {
        await this.disconnectAsync();
        return;
      }

      if (!this.isConnected) {
        await this.connectAsync(tokenInfo);
      }

      await this.subscribeOrganizationsAsync(selectedOrganizations);
    });
  }

  public async disconnectAsync(): Promise<void> {
    console.log('disconnect');
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      return await this.connection.stop();
    }
    this.isConnected = false;
    return;
  }

  private isDisconnected(): boolean {
    return !this.connection || this.connection.state === signalR.HubConnectionState.Disconnected
  }

  private async connectAsync(tokenInfo: TokenInfo): Promise<void> {
    // Подключение уже идёт — дожидаемся его, а не выходим сразу: иначе следом за выходом
    // пойдёт подписка на ещё не открытое соединение.
    if (this.connectPromise) {
      return await this.connectPromise;
    }

    this.connectPromise = this.tryConnectAsync(tokenInfo);

    try {
      await this.connectPromise;
    }
    finally {
      this.connectPromise = null;
    }
  }

  private async tryConnectAsync(tokenInfo: TokenInfo): Promise<void> {
    if (!this.connection || this.isDisconnected()) {
      this.connection = this.createConnection(tokenInfo);
      console.log('connect');
    }

    if (!this.isDisconnected())
    {
      return;
    }

    try {
      await this.connection.start();

      this.eventService.registerEvents(this.connection);

      this.isConnected = true;

      // Подписка после переподключения: поток организаций больше эмитить не обязан,
      // а хеш прошлой неудачной попытки не сохранён — подписаться некому, кроме нас.
      await this.subscribeOrganizationsAsync(
        this.ngxsStore.selectSnapshot(OrganizationsState.getSelectedOrganizationTree));
    }
    catch (connectionError) {
      if (connectionError.message && connectionError.message?.includes('Status code \'401\''))
      {
        try {
          const refreshTokenResponse = await firstValueFrom(this.apiService.refreshToken({refreshToken: tokenInfo.refreshToken}));
          const tokenParts = this.apiService.decodeToken(refreshTokenResponse.data.authToken);

          const newTokenInfo: TokenInfo = {
            token: refreshTokenResponse.data.authToken,
            refreshToken: refreshTokenResponse.data.refreshToken,
            tokenExpires: tokenParts.tokenExpires,
          };

          this.store.dispatch(refreshLoginData({
            tokenInfo: newTokenInfo,
            userData: refreshTokenResponse.data.userData
          }));
          await this.tryConnectAsync(newTokenInfo);
        }
        catch (refreshError){
          await this.disconnectAsync();
          this.store.dispatch(signOutUser());
        }
      }
      else{
        console.log('Error while starting connection: ', connectionError);
        setTimeout(() => this.connectAsync(tokenInfo), 5000);
      }
    }
  }

  private createConnection(tokenInfo: TokenInfo): signalR.HubConnection
  {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${environment.baseUrl}/${ApiSignalUrl}`, {accessTokenFactory: () => tokenInfo.token})
      .build();
  }

  private async subscribeOrganizationsAsync(selectedOrganizations: Organization[]): Promise<void>
  {
    const selectedOrganizationsIds = (selectedOrganizations ?? []).map((organization) => organization.id);
    const hashSelectedOrganizations = selectedOrganizationsIds.join('|');

    if (!selectedOrganizationsIds.length || hashSelectedOrganizations === this.lastHashSelectedOrganizations) {
      return;
    }

    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('OrganizationSubscribe', selectedOrganizationsIds);
      // Хеш запоминаем только после успешной подписки: иначе следующая попытка будет
      // пропущена по совпадению хеша и клиент останется без обновлений по организациям.
      this.lastHashSelectedOrganizations = hashSelectedOrganizations;
    }
    catch (subscribeError) {
      console.log('Error while subscribing to organizations: ', subscribeError);
    }
  }
}
